/* Серверный режим «Рилс с вами» и «Вы в кино» (reels) на движке шаблона (план вехи 3 §0 D1, план рилса §2).
   createReels(createReelsServer) (features/reels/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SV спрашивает этот объект (SRV):
   каталог, цена, готовность, люди, фото предмета, создание и хвост — только из формы и сервиса заказа кадра (OrderService привязки EngineFrame).
   Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё S, свой «Вы» экрана (ctx.me: роль «Вы»), свой мост загрузок, своё зеркало входов; файлы, согласие и заказ одного приложения
     или владельца не видны и не уходят под другим;
   - таблицы шаблона (разделы, «кто нужно», типы, роли, шаблоны, пакет) — из формы (reels-catalog.ts), переписываются на месте при смене
     формы или кадра; до формы каталог пуст, смена вида ждёт готовности (bound.ready);
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис; при открытии ничего
     не отправляется, только показ;
   - S → входы: одна точка — sync() движка (commit, inputs-mirror: под замком не пишет, равные входы не пишет); входы → S — только когда
     входы сменились не зеркалом (повтор из «Истории», «Изменить» после перезагрузки, заявка родной формы): reels-inputs.viewOf → S;
     id фото предмета пишет мост загрузок (лотки по видам: JSON-ключ objects или поля <вид>_<N>), зеркало их не сравнивает;
   - фото сначала идёт серверу: политика поля, лист согласия, одна очередь загрузок (media-bridge); плитка — файл моста; превью повтора —
     с сервера (не вышло — загрузить заново, решение Q7);
   - цена на кнопке — цена каталога (шаблон, набор со скидкой, пакет); в заказ уходит цена сервиса; если они расходятся у платной работы,
     создание закрыто: «Цена этого выбора пока не подтверждена» (родная форма, D11);
   - близкий без сохранения в заказ не уходит (форма контракт person не публикует: Q-C2), неготовый — причина сервера;
   - тексты: свои строки адаптера — словарём профиля (uiText, uiFormat). */
import { endpoints } from '../../api';
import type { SpeechOrder } from '../../api/speech';
import { accountLocale, uiFormat, uiText } from '../../i18n';
import { FILTER_SUB, openFilterSheet } from '../../ui/filter-sheet';
import { circleBalance, forgetCircleBalance, rememberCircleBalance } from '../studio/circle/tool-helpers';
import type { StudioApp, StudioForm } from '../studio/studio-api';
import { record, rows, safeMediaUrl, text } from '../studio/studio-content';
import { openStudioTarget } from '../studio/studio-navigation';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaNote, mediaPolicy, type MediaBridge, type MediaItem, type MediaPolicy, type MediaTray } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { details, detailsKey, leaseSkins, loadDetails, skinUrl } from '../studio/engine-kit/char-cards';
import { castSel, meState, peopleModule, performerRef, personRef, SILHOUETTE, watchPeople, type MeState, type SnapCast } from '../studio/engine-kit/people-bridge';
import { createResultMedia, type ResultMedia } from '../studio/engine-kit/result-media';
import { archive, archiveUrl } from '../studio/engine-kit/result-gallery';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { keyInfo } from './form-keys';
import { emptyTables, tablesFrom, tplRole, writeTables, type EngineTables, type Tables, type TplPreset, type TplRole } from './reels-catalog';
import { applyPatch, canMulti, castPatch, castSkins, objectFields, objectsPatch, selectionPatch, uploadKeys, viewOf, type CastLine } from './reels-inputs';
import { reelsPolicy, reelsSupported, screenRoles, objectKind, type ReelsMode, type ReelsPolicy } from './reels-policy';
import { JSON_CAST } from './reels-cast';
import { MORE_NOTES, postMore } from './reels-more';
import { resultRows } from './reels-result-model';

/* ---------- контракт с движком (features/reels/engine.ts, serve({...})) ---------- */

type Inputs = Record<string, string>;
/** Фото лотка в S.obj: id плитки, адрес, кадрирование; m — запись моста загрузок */
export interface ReelsTile {
  id: number;
  url: string;
  name: string;
  pos: number;
  f: string;
  m?: string;
}
/** Апка-сцена шаблона (S.app) */
export interface ReelsApp {
  id: number;
  n: string;
  k: number;
  f: string;
  cast: TplRole[];
  dur?: number;
  d?: string;
}
/** Человек в кадре (S.people): id близкого или 'c:<id>' персонажа; role — место роли */
export interface ReelsSel {
  id: number | string;
  act: string | null;
  own: string;
  role?: string | null;
}
export interface ReelsState {
  view: 'cat' | 'pre' | 'multi';
  cat: string;
  back: string;
  fromCat: boolean;
  pid: string;
  sel: string[];
  selMode: boolean;
  app: ReelsApp | null;
  sty: number;
  people: ReelsSel[];
  pool: ReelsSel[];
  extra: unknown[];
  obj: Record<string, ReelsTile[]>;
  top: Record<string, number>;
}
/** Лоток вида: k, сколько нужно и сколько берётся (objNeeds шаблона) */
export interface ObjNeed {
  k: string;
  min: number;
  max: number;
}
/** Место роли шаблона (castPlan) */
export interface PlanRole extends TplRole {
  grp?: string;
}
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  [key: string]: unknown;
}
interface EnginePeople {
  formHTML(): string;
  paintForm(): void;
  missing(): string;
  castMiss(): string;
  snap(): { cast: SnapCast[]; [key: string]: unknown };
  prune(): void;
  close(): void;
  focusRole(id: string | null): void;
  readonly isOpen: boolean;
}
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
/** Крючки, которые движок отдаёт serve (engine.ts, блок SV перед BotPeople.attach): части экрана ниже по файлу — геттеры */
export interface ReelsEngineHooks {
  S: ReelsState;
  CAM: { st: string; k: string | null; stream: unknown; sim: boolean };
  PV: { i: number; key: string; n: number; t: number };
  PRV: { el: HTMLElement | null; id: string | null };
  OBJ: string[];
  tables: EngineTables;
  setOffer(o: Tables['offer']): void;
  offer(): { vars: number; packN: number; multiFrom: number; multiOff: number; maxPh: number };
  readonly P: EnginePeople;
  readonly tail: BotRunApi;
  foot: EngineFoot;
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  pre(): TplPreset;
  mode(): ReelsMode;
  selPs(): TplPreset[];
  pplTs(): TplPreset[];
  needTs(): TplPreset[];
  objNeeds(): ObjNeed[];
  castPlan(): { cast: PlanRole[]; map: Record<string, string[]> };
  /** Фото, из которых собран ролик it (picsOf шаблона): люди его ролей или фото предмета, без своих — заглушка blank() */
  picsOf(sn: Record<string, unknown>, it: Record<string, unknown>): Array<{ url: string; op: string; f: string }>;
  /** Надписи поверх кадра (decHTML шаблона) по ov/tx шаблона */
  decHTML(p: { ov?: string; tx?: string }): string;
  castFlat(): Array<{ role: string; rn: string; type: string; id: unknown; name?: string }>;
  doneH(r: BotRunRun): string;
  setup(o: Record<string, unknown>): void;
  render(): void;
  go(): void;
  catRefresh(anim?: boolean): void;
  backToCat(): void;
  prvClose(now?: boolean): void;
  camStop(): void;
  camClose(): void;
  upPaint(k: string): void;
  filesChanged(k: string): void;
  note(x: ObjNeed, text: string): void;
  sync(): void;
  whoPaint(): void;
  pvPaint(): void;
  inputsPaint(): void;
  resPaint(gi: number, fresh: boolean): void;
  rcPaint(): void;
  fitPeople(): void;
  setPeople(v: ReelsSel[], why?: string): void;
  /** Карточка «Ещё настройки» на месте (BotCore.settingsCard.sync): пришла карточка персонажа или картинка вида */
  syncSet(): void;
}
/** Чего не хватает (missInfo шаблона): c — код ('' — можно), x — лоток, t — текст сервера */
export interface MissInfo {
  c: string;
  x?: ObjNeed;
  t?: string;
}
/** Кадр примера в рамке формы (pvList шаблона) */
export interface PvFrame {
  k: string;
  url: string;
  pos: number;
  x: number;
  f: string;
  tag?: string;
}
/** Ряд результата (groupsOf шаблона) */
export interface ResultGroup {
  gi: number;
  /** Ряд заказа (template_id ряда video_set, как у родного хвоста) */
  id: string;
  h: string;
  items: Array<{ i: number; it: Record<string, unknown>; cap: string; sub: string; index?: number }>;
}

export interface ReelsServer {
  ui<U extends object>(base: U): ServerUi<U>;
  entry(o: unknown): Record<string, unknown>;
  open(o: unknown): boolean;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  catReady(): void;
  /** Открыта сцена «Вы в кино» (policy.scene), а не апка без своих ролей */
  scene(): boolean;
  pre(): TplPreset;
  missInfo(): MissInfo;
  price(): number;
  label(): string | null;
  commit(): void;
  /** Слоты карточки «Ещё настройки» (BotCore.settingsCard): «Вид персонажа» у персонажей STEOS состава */
  setItems(): unknown[];
  styles(a: unknown): Array<{ n: string; f: string }>;
  cover(p: TplPreset): Array<{ url: string; pos: number; f: string }>;
  fan(n?: number): string[];
  comparePrice(): number;
  tplPrice(p: TplPreset): number;
  appFrames(): PvFrame[];
  addFiles(k: string, list: ArrayLike<File | Blob | Record<string, unknown>>): void;
  removeFile(k: string, tile: ReelsTile): boolean;
  camOff(k: string): void;
  meState(): MeState;
  blank(): string;
  start(btn: HTMLElement): void;
  groups(r: BotRunRun): ResultGroup[];
  pic(sn: Record<string, unknown>, v: ResultGroup['items'][number]): string;
  groupMore(r: BotRunRun, gi: number): number | null;
  more(gi: number, btn: HTMLElement): void;
  toCircle(): void;
  save(btn: HTMLElement): void;
  leave(go: () => void): void;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null, e?: Event): boolean;
  /** Кнопка ⇅ шапки каталога (лист «Сортировка и фильтры») и сетка раздела с её выбором */
  sortBtn(): string;
  catView(list: TplPreset[]): TplPreset[];
}

/* ---------- общие части ---------- */

const MB = 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const TRANSPORT = uploadFailureText(null);
/** Пустой кадр обложки шаблона без примера (картинок прототипа в production нет, D12) */
const BLANK_FRAME = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
/** Шаблон, которого ещё нет (каталог не пришёл или пуст): форма без выбора — «Этот вариант рилса пока недоступен» */
const STUB: TplPreset = { id: '', cat: '', i: 0, n: '', sub: '', nd: 'me', min: 0, max: 0, d: 0, t: '', b: '', ov: '', tx: '', f: '', frames: [], price: null, more: null };
const OFF = 'Этот вариант рилса пока недоступен';
const WAIT = 'Проверяем доступность';
/** «Занята» кнопка шаблона (SteosUI.busy): крутилка, пока идёт действие */
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
/** Сколько ждать роликов дозаказа опросом заказа (GET), прежде чем снять заготовки ряда: ролики придут в чат и в «Историю» */
const MORE_WAIT_MS = 5 * 60 * 1000;
const MORE_DELAYS = [0, 2000, 3000, 5000, 8000, 10000];
/** Смена страницы SteosUI.swap идёт 170 мс: закрыть или сменить заказ BotRun раньше — нельзя (см. фасад хвоста) */
const SWAP_GUARD_MS = 400;
/** Веер пакета эталона (bot-reels.js packHTML / ghostsHTML) */
const FAN = ['film', 'bw', 'vhs'];
const isImage = (f: unknown): f is File => f instanceof Blob && (!f.type || /^image\//.test(f.type));

/** Таблицы по форме: одна запись на приложение, версию шаблона и язык (второй кадр того же приложения рисуется сразу) */
const tablesCache = new Map<string, Tables>();
const words = () => ({
  hit: uiText('Хит'),
  fresh: uiText('Новое'),
  /* у эталона пакет из десяти — подзаголовок словом («Десять разных сюжетов…», bot-reels.js PL pack); иное число — цифрой */
  packSub: (n: number) => (n === 10 ? uiText('Десять разных сюжетов с вами за один заказ') : uiFormat('{0} разных сюжетов с вами за один заказ', n)),
  t: (value: string) => uiText(value),
});
const localeKey = () => accountLocale();

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  mirror: InputsMirror;
  /** Форма, для которой прошла первая сверка (таблицы, вход экрана, входы → S) */
  form: StudioForm | null;
  policy: ReelsPolicy | null;
  /** Ключ таблиц формы этого кадра (форма, версия, язык) */
  tables: string;
  /** Входы без id загрузок, которые S уже знает (своя запись зеркала или перенос в S) */
  known: Inputs | null;
  /** Входы первой сверки без id загрузок (подстановка входа и повтора) */
  prefill: Inputs | null;
  intentSeen: string;
  formWait: boolean;
  formAt: StudioForm | null;
  /** «Вы» экрана для ctx.me BotPeople: роль «Вы» в сюжете (своя у кадра) */
  me: MeState;
  /** Роль, которую выбрали на листе сцены («Выбрать, кто играет», open_role) */
  openRole: string | true | null;
  /** Подпись ролей формы, для которой «Вы» этого кадра уже расставлен (autoMe) */
  castSig: string;
  /** Вид персонажа STEOS в составе: id персонажа → id вида (нет — вид по умолчанию); уходит в cast[].skin_id */
  skins: Map<number, number>;
  /** Лотки моста по имени → вид фото предмета */
  trays: Map<string, string>;
  /** Плитка, которую ждёт запись моста (восстановление: id загрузки → вид) */
  restoring: Set<string>;
  said: string;
  consent: ConsentSheet | null;
  peek: Intent | null;
  /** Ход хвоста по сервису кадра */
  driver: RunDriver;
  /** Ролики итога (owner-protected файлы, один <video> в рамке варианта) */
  result: ResultMedia;
  /** Таймеры опроса дозаказов «Ещё 3 варианта» */
  polls: Set<number>;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  ready: Promise<void>;
  readyDone(): void;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createReelsServer(h: ReelsEngineHooks): ReelsServer {
  const { S } = h;
  /** Начальное S шаблона: новый кадр начинает с него */
  const S0 = JSON.stringify(S);
  /* таблицы шаблона — демо-данные: без формы сервера каталога нет */
  writeTables(h.tables, emptyTables());
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относятся S и экземпляр хвоста (объект, а не ключ: ключи повторяются после нового входа в раздел) */
  let painted: Frame | null = null;
  /** Ключ таблиц, которые сейчас стоят в движке */
  let loaded = '';
  /** setup шаблона с входом сервера (entry отдаёт его как есть); иначе — пустой вход (вход открытия решает сервер) */
  let passing = false;
  let tileSeq = 100000;
  /** «Вы» без кадра (кадр не активен): роль не помнится */
  const loose = meState();

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  const swapping = () => !!h.root()?.classList.contains('leave');
  /** Подсказка-стекло у элемента; элемента нет на месте — у кнопки подвала */
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  /** Входы без id загрузок: их пишет мост, переносить в S нечего */
  const strip = (f: Frame, inputs: Inputs): Inputs => {
    const out = { ...inputs };
    for (const key of uploadKeys(f.svc.get().form ?? f.form)) delete out[key];
    return out;
  };
  /** Входы заявки — строками, как во входах сервиса (тело заявки держит JSON-ключи объектами, а перечисления — без кавычек) */
  function encodeBack(form: StudioForm, inputs: Record<string, unknown>): Inputs {
    const out: Inputs = {};
    for (const [key, value] of Object.entries(inputs)) {
      if (typeof value !== 'string') {
        out[key] = JSON.stringify(value);
        continue;
      }
      if (!keyInfo(form, key)?.json) {
        out[key] = value;
        continue;
      }
      try {
        JSON.parse(value);
        out[key] = value;
      } catch {
        out[key] = JSON.stringify(value);
      }
    }
    return out;
  }

  /* ---- таблицы ---- */
  function tablesKey(f: Frame, form: StudioForm) {
    return `${f.app.id}:${form.template_version ?? ''}:${localeKey()}:${JSON.stringify(record(form.constraints).reels ?? null).length}`;
  }
  /** Таблицы формы кадра в движке: из памяти или заново; S — в пределах нового каталога */
  function load(f: Frame, form: StudioForm) {
    const key = tablesKey(f, form);
    f.policy = reelsPolicy(form, f.app);
    if (loaded === key) {
      f.tables = key;
      return false;
    }
    let t = tablesCache.get(key);
    if (!t) {
      t = tablesFrom(form, f.policy, words());
      tablesCache.set(key, t);
    }
    writeTables(h.tables, t);
    h.setOffer(t.offer);
    loaded = key;
    f.tables = key;
    const { CATN } = h.tables;
    if (!CATN[S.cat]) S.cat = CATN.foryou ? 'foryou' : (h.tables.CATS[0]?.[0] ?? S.cat);
    if (!CATN[S.back]) S.back = S.cat;
    S.sel = S.sel.filter(id => h.tables.PRESETS[id]);
    return true;
  }
  /** Пустой каталог (кадр без формы): демо-данных шаблона нет */
  function unload() {
    if (!loaded) return;
    writeTables(h.tables, emptyTables());
    loaded = '';
  }

  /* ---- вход экрана ---- */
  /** Вход кадра: сцена («Вы в кино», роли шаблона) или инструмент с каталогом (старый ключ студии preset, иначе раздел) */
  function enter(f: Frame) {
    const p = f.policy!;
    const init = f.b.initialInputs ?? {};
    const role = typeof init.open_role === 'string' && init.open_role ? init.open_role : null;
    f.openRole = role ?? (f.b.templateEntry ? true : null);
    let o: Record<string, unknown>;
    if (p.mode === 'app' || (!p.templates.length && !!p.template)) {
      const t = p.template;
      const cast = (p.cast ?? []).map(tplRole).filter((x): x is TplRole => !!x);
      const app: ReelsApp = {
        id: f.app.id,
        n: f.app.name || t?.title || '',
        k: 1,
        f: 'reels',
        cast,
        ...(t?.durationS ? { dur: t.durationS } : {}),
        ...(t?.plot ? { d: t.plot } : {}),
      };
      o = { app, sty: 0 };
    } else {
      const preset = typeof init.preset === 'string' ? init.preset : '';
      o = preset ? { preset } : { cat: h.tables.CATN[S.cat] ? S.cat : (h.tables.CATS[0]?.[0] ?? '') };
    }
    passing = true;
    try {
      h.setup(o);
    } finally {
      passing = false;
    }
    h.P.prune();
  }
  /** Роль сцены — в видимую часть колонки, когда экран встал (смена экрана 170 мс и проявление), как open шаблона */
  function focusRole(f: Frame) {
    const want = f.openRole;
    f.openRole = null;
    if (!want || S.view === 'cat' || !h.pplTs().length) return;
    window.setTimeout(() => {
      if (cur() !== f || !onForm() || S.view === 'cat') return;
      const id = typeof want === 'string' && h.castPlan().cast.some(r => r.id === want) ? want : null;
      h.P.focusRole(id);
    }, 380);
  }

  /* ---- входы ↔ S ---- */
  /** Состав формы из BotPeople: место роли → role_id (до «|» у набора) и шаблоны набора, где роль играет */
  function castLines(): CastLine[] {
    if (!h.pplTs().length) return [];
    const plan = h.castPlan();
    const multi = h.mode() === 'multi';
    const skins = cur()?.skins;
    const out: CastLine[] = [];
    for (const row of h.P.snap().cast ?? []) {
      const role = row.role.split('|')[0];
      const templates = multi ? Object.keys(plan.map).filter(t => plan.map[t].includes(row.role)) : [];
      for (const p of row.who) {
        const ref = performerRef(p);
        if (!ref || out.some(line => line.ref === ref)) continue;
        const skin = skins?.get(charOf(ref) ?? 0);
        out.push(skin ? { role, ref, templates, skin } : { role, ref, templates });
      }
    }
    return out;
  }
  /** Персонаж STEOS ссылки состава (character:<id>) */
  const charOf = (ref: string) => (/^character:[1-9][0-9]*$/.test(ref) ? Number(ref.slice('character:'.length)) : null);

  /* ---- карточка «Ещё настройки»: вид персонажа ---- */
  /** Карточки персонажей, которые сейчас грузятся (один запрос и одна перерисовка на персонажа) */
  const cardWait = new Set<number>();
  const setPaint = (f: Frame) => {
    if (cur() === f && onForm()) h.syncSet();
  };
  /** Слоты (BotCore.settingsCard, как «Вид персонажа» у «Говорящего кружка»; решение 29.10.2026): у каждого персонажа STEOS состава — свой «Вид
      персонажа» (тип faces: первым — вид по умолчанию, затем виды карточки каталога; видов у персонажа ещё нет — один вид по умолчанию, блок всё равно
      стоит); персонажей несколько — заголовок слота их имя. Голоса нет: сцена n8n его не принимает (docs/91). Слота нет, пока контракт состава
      не публикует skin_id или карточка персонажа не пришла */
  /* ---- каталог: лист «Сортировка и фильтры» (владелец 02.11.2026: ⇅ в шапке, как у «Озвучки» и «Всех шаблонов») ---- */
  /** Порядок и «Кто в кадре» (несколько сразу; пусто — все). Режим «Выбрать несколько» и пакет «10 рилсов» — как были */
  const flt: { sort: string; cast: string[] } = { sort: 'pop', cast: [] };
  /** Пункт «Кто в кадре» → кого шаблон ждёт (nd шаблона) */
  const CAST_OF: Record<string, string[]> = { one: ['me', 'one'], two: ['pair'], many: ['family'], kid: ['kid'], pet: ['pet'], obj: ['goods', 'food', 'place', 'pics'] };
  const CAST_ITEMS: Array<[string, string, string]> = [
    ['one', 'Один', 'Вы или один близкий'],
    ['two', 'Двое', 'Вы и близкий'],
    ['many', 'Несколько', 'Вы и близкие'],
    ['kid', 'Дети', 'С ребёнком'],
    ['pet', 'Питомцы', 'С питомцем'],
    ['obj', 'Товары и места', 'Фото товара, блюда или места'],
  ];
  const SORTS = ['pop', 'new', 'cheap', 'short', 'name', 'name_desc'];
  const SORT_NAMES: Record<string, string> = { pop: 'Популярные', new: 'Новые', cheap: 'Дешевле', short: 'Короче', name: 'От А до Я', name_desc: 'От Я до А' };
  const castFits = (p: TplPreset, keys: string[]) => !keys.length || keys.some(k => CAST_OF[k]?.includes(p.nd));
  function catView(list: TplPreset[]): TplPreset[] {
    const out = list.filter(p => castFits(p, flt.cast));
    const order = (a: TplPreset, b: TplPreset) => a.i - b.i;
    const by: Record<string, (a: TplPreset, b: TplPreset) => number> = {
      new: (a, b) => Number(b.b === uiText('Новое')) - Number(a.b === uiText('Новое')) || order(a, b),
      cheap: (a, b) => server.tplPrice(a) - server.tplPrice(b) || order(a, b),
      short: (a, b) => a.d - b.d || order(a, b),
      name: (a, b) => a.n.localeCompare(b.n, 'ru') || order(a, b),
      name_desc: (a, b) => b.n.localeCompare(a.n, 'ru') || order(a, b),
    };
    return flt.sort === 'pop' ? out : out.slice().sort(by[flt.sort] ?? order);
  }
  function openSort() {
    const section = () => {
      const PL = h.tables.PL;
      return h.S.cat === 'foryou' ? PL.filter(p => (p.cat === 'foryou' ? p.t !== 'x' : p.b === uiText('Хит'))) : PL.filter(p => p.cat === h.S.cat);
    };
    openFilterSheet({
      groups: () => [
        { key: 'sort', label: uiText('Сначала'), items: SORTS.map(id => ({ id, label: uiText(SORT_NAMES[id]), sub: uiText(FILTER_SUB[id] || '') })), value: [flt.sort], def: ['pop'] },
        {
          key: 'cast',
          label: uiText('Кто в кадре'),
          multi: true,
          items: CAST_ITEMS.map(([id, label, sub]) => ({ id, label: uiText(label), sub: uiText(sub), disabled: !section().some(p => castFits(p, [id])) })),
          value: flt.cast.slice(),
          def: [],
        },
      ],
      t: { title: uiText('Сортировка и фильтры'), close: uiText('Закрыть'), reset: uiText('Сбросить'), done: uiText('Готово') },
      onChange: (key, value) => {
        if (key === 'sort') flt.sort = value[0] || 'pop';
        else flt.cast = value.filter(id => CAST_OF[id]);
        const btn = h.root()?.querySelector('[data-rl-sort]');
        btn?.classList.toggle('on', flt.sort !== 'pop' || flt.cast.length > 0);
        h.catRefresh(true);
      },
    });
  }
  function setItems(): unknown[] {
    const f = cur();
    const form = f?.svc.get().form;
    if (!f || !form || S.view === 'cat' || !castSkins(form)) return [];
    const ids = [...new Set(castLines().flatMap(line => charOf(line.ref) ?? []))];
    const cards = ids.flatMap(id => {
      const card = details.get(detailsKey(id));
      if (card === undefined && !cardWait.has(id)) {
        cardWait.add(id);
        void loadDetails(id)
          .then(c => {
            if (c) leaseSkins(c, () => setPaint(f));
          })
          .finally(() => {
            cardWait.delete(id);
            setPaint(f);
          });
      }
      return card ? [card] : [];
    });
    return cards.map(card => {
      const id = card.avatar.id;
      leaseSkins(card, () => setPaint(f));
      const base = safeMediaUrl(card.avatar.image_thumb_url || card.avatar.image_url) ?? SILHOUETTE;
      const face = (url: string | null) => `<img src="${h.esc(url || base)}" alt="" draggable="false">`;
      const chosen = f.skins.get(id);
      return {
        k: `skin:${id}`,
        t: cards.length > 1 ? card.avatar.name : uiText('Вид персонажа'),
        kind: 'faces',
        cur: chosen && card.avatar.skins.some(sk => sk.id === chosen) ? String(chosen) : 'def',
        opts: [['def', uiText('Обычный'), '', face(base)], ...card.avatar.skins.map(sk => [String(sk.id), sk.name, '', face(skinUrl(card, sk.id))])],
        set: (v: string) => {
          const skin = Number(v);
          if (Number.isSafeInteger(skin) && skin > 0) f.skins.set(id, skin);
          else f.skins.delete(id);
        },
      };
    });
  }
  /** Выбор S в режим и шаблоны формы */
  function selection(): { mode: ReelsMode; id: string; ids: string[] } {
    const mode = h.mode();
    return {
      mode,
      id: mode === 'one' ? h.pre().id : '',
      ids: mode === 'multi' ? S.sel.filter(id => h.tables.PRESETS[id]) : [],
    };
  }
  /** «Вы» — в первую свободную роль «для вас» без пола, когда роли формы сменились (правило castSync BotPeople). BotPeople помнит подпись
      ролей на всё подключение, а «Вы» экрана у каждого кадра свой (ctx.me): новый кадр с теми же ролями, что у прежнего, сам «Вы» не ставит.
      Роли из входов (повтор, заявка) уже расставлены — их подпись помечается в adopt. → true, если роль «Вы» поставлена */
  const castSigOf = (cast: PlanRole[]) => cast.map(r => `${r.id}:${r.kinds.join(',')}:${r.g ?? ''}:${r.max}`).join('|');
  function autoMe(f: Frame): boolean {
    if (S.view === 'cat' || !h.pplTs().length) return false;
    h.P.snap();
    const cast = h.castPlan().cast;
    const sig = castSigOf(cast);
    if (sig === f.castSig) return false;
    f.castSig = sig;
    if (f.me.role || !peopleModule()?.me.get().ready) return false;
    const fitsMe = (r: PlanRole) => r.kinds.includes('adult') || r.kinds.includes('old');
    const r = cast.find(x => x.me && !x.g && fitsMe(x) && S.people.filter(p => p.role === x.id).length < x.max);
    if (!r) return false;
    f.me.role = r.id;
    return true;
  }
  /** S → входы (sync шаблона): под замком, на хвосте и в каталоге не пишется; равные входы — тот же объект */
  function commit(f: Frame | null = cur()) {
    if (!f || !f.form || !h.mounted() || h.tail.run || S.view === 'cat') return;
    const s = f.svc.get();
    const form = s.form;
    if (!form || f.svc.derive(s).locked) return;
    if (autoMe(f)) h.P.paintForm();
    const patch = { ...selectionPatch(form, selection()), ...castPatch(form, castLines()) };
    f.mirror.commit(prev => applyPatch(form, prev, patch));
    f.known = strip(f, f.svc.get().inputs);
  }
  /** Места ролей для состава из входов: у набора — «роль|подпись» того шаблона, где роль играет */
  function slotOf(line: CastLine, plan: ReturnType<ReelsEngineHooks['castPlan']>): string | null {
    const slots = plan.cast.filter(r => r.id === line.role || r.id.startsWith(line.role + '|'));
    if (!slots.length) return null;
    const mine = slots.find(r => line.templates.some(t => (plan.map[t] ?? []).includes(r.id)));
    return (mine ?? slots[0]).id;
  }
  /** Входы → S: режим и шаблоны, состав (люди по ролям и роль «Вы»), фото предмета (превью повтора) */
  function adopt(f: Frame, inputs: Inputs) {
    const form = f.svc.get().form ?? f.form;
    if (!form || !f.policy) return;
    const v = viewOf(form, inputs, f.policy);
    const { PRESETS } = h.tables;
    if (v.mode === 'one' && PRESETS[v.id]) {
      S.app = null;
      S.view = 'pre';
      S.pid = v.id;
      S.back = PRESETS[v.id].cat || S.back;
      S.fromCat = false;
    } else if (v.mode === 'multi' && v.ids.some(id => PRESETS[id])) {
      const ids = v.ids.filter(id => PRESETS[id]);
      S.app = null;
      if (ids.length === 1) {
        S.view = 'pre';
        S.pid = ids[0];
      } else {
        S.view = 'multi';
        S.sel = ids;
        S.selMode = true;
      }
      S.fromCat = false;
    } else if (v.mode === 'pack' && PRESETS.pack && keyInfo(form, 'mode') && !S.app) {
      S.view = 'pre';
      S.pid = 'pack';
      S.fromCat = false;
    }
    if (S.view !== 'cat' && v.cast.length) {
      const plan = h.castPlan();
      const bySlot: Record<string, string[]> = {};
      for (const line of v.cast) {
        const slot = slotOf(line, plan);
        if (slot) (bySlot[slot] ??= []).push(line.ref);
      }
      f.skins.clear();
      for (const line of v.cast) {
        const id = charOf(line.ref);
        if (id && line.skin) f.skins.set(id, line.skin);
      }
      const { sel, meRole } = castSel(bySlot);
      f.me.role = meRole;
      f.castSig = castSigOf(plan.cast);
      h.setPeople(sel.map(x => ({ id: x.id, act: null, own: '', role: x.role ?? null })), 'set');
    }
    restoreObjects(f, v.objects);
    f.known = strip(f, inputs);
    f.mirror.seen(inputs);
  }

  /* ---- фото предмета: лотки моста по видам ---- */
  const objectsJson = (form: StudioForm | null | undefined) => !!keyInfo(form, 'objects')?.json;
  /** Лоток вида k: список JSON-ключа objects (objects.<вид>) или поля <вид>_<N>; null — форма вид не публикует */
  function trayOf(f: Frame, k: string): MediaTray | null {
    const form = f.svc.get().form;
    if (!form) return null;
    let tray: MediaTray | null = null;
    if (objectsJson(form)) tray = f.bridge.slots({ list: `objects.${k}`, max: 20 });
    else {
      const keys = objectFields(form)[k];
      if (keys?.length) tray = f.bridge.slots(keys);
    }
    if (tray) f.trays.set(tray.name, k);
    return tray;
  }
  /** Политика JSON-списка objects.<вид>: загрузка свойства (upload) контракта, по умолчанию — фото до 20 МБ */
  function objectPolicy(f: Frame, key: string): MediaPolicy | null {
    const form = f.svc.get().form;
    if (!key.startsWith('objects.')) return mediaPolicy(form, key);
    const field = (form?.screen_fields ?? []).find(item => item.key === 'objects') as unknown as Record<string, unknown> | undefined;
    if (!form || !field) return null;
    const prop = record(record(field.properties)[key.slice('objects.'.length)]);
    const upload = record(prop.upload);
    const accept = Array.isArray(upload.accept) && upload.accept.length ? upload.accept.filter((x): x is string => typeof x === 'string') : PHOTO_TYPES;
    const maxBytes = typeof upload.max_bytes === 'number' && upload.max_bytes > 0 ? upload.max_bytes : 20 * MB;
    const purposes = Array.isArray(upload.purposes) ? upload.purposes.filter((x): x is string => typeof x === 'string') : [];
    return {
      key,
      kind: 'photo',
      accept,
      maxBytes,
      minWidth: null,
      minHeight: null,
      minMs: null,
      maxMs: null,
      note: mediaNote(accept, maxBytes),
      raw: { kind: 'photo', purpose: purposes[0] ?? 'order_input' },
    };
  }
  /** Лоток, которому принадлежит ключ (objects.<вид> или поле вида) */
  const kindOfKey = (key: string) => (key.startsWith('objects.') ? key.slice('objects.'.length) : objectKind(key));
  const needOf = (k: string) => h.objNeeds().find(x => x.k === k) ?? null;
  /** Превью повтора: id загрузок вида, которых в лотке ещё нет */
  function restoreObjects(f: Frame, objects: Record<string, string[]>) {
    for (const [k, ids] of Object.entries(objects)) {
      const tray = trayOf(f, k);
      if (!tray || !ids.length) continue;
      const have = new Set(tray.list().map(item => item.uploadId));
      if (ids.every(id => have.has(id))) continue;
      if (S.obj[k]) S.obj[k] = S.obj[k].filter(tile => !tile.m);
      void tray.restore(ids).finally(() => {
        if (cur() === f && onForm()) h.filesChanged(k);
      });
    }
  }
  /** Плитка записи моста */
  function putTile(f: Frame, item: MediaItem, trayName: string) {
    const k = f.trays.get(trayName) ?? kindOfKey(item.key);
    const list = (S.obj[k] ??= []);
    if (list.some(tile => tile.m === item.id)) return;
    list.push({ id: ++tileSeq, url: item.url || BLANK_FRAME, name: item.name, pos: 44, f: '', m: item.id });
    if (cur() === f && onForm()) h.filesChanged(k);
  }
  function dropTile(f: Frame, item: MediaItem | null, key: string) {
    const k = kindOfKey(key);
    const list = S.obj[k];
    if (!item || !list) return;
    const at = list.findIndex(tile => tile.m === item.id);
    if (at < 0) return;
    list.splice(at, 1);
    if (cur() === f && onForm()) h.filesChanged(k);
  }
  /** Запись JSON-списка objects.<вид>: id готовых загрузок вида в objects (не больше предела лотка) */
  function writeObjects(f: Frame, key: string, value: string | null) {
    const form = f.svc.get().form;
    if (!form || !key.startsWith('objects.')) return;
    const k = key.slice('objects.'.length);
    let ids: string[] = [];
    try {
      const parsed: unknown = value ? JSON.parse(value) : [];
      ids = Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      ids = [];
    }
    f.svc.setters.inputs(current => {
      const next = applyPatch(form, current, objectsPatch(form, current, k, ids));
      return sameInputs(next, current) ? current : next;
    });
  }

  /* ---- готовность ---- */
  function missInfo(): MissInfo {
    const f = cur();
    if (!f) return { c: 'load', t: uiText(WAIT) };
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return { c: '' };
    if (!s.identity && !s.integrityError) return { c: 'load', t: uiText(WAIT) };
    if (s.integrityError) return { c: 'blocked', t: uiText(s.integrityError) };
    if (!s.identity || s.identity.appId !== f.app.id) return { c: 'load', t: uiText(WAIT) };
    /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
    if (!s.form) return s.error ? { c: '' } : { c: 'load', t: uiText(WAIT) };
    if (s.versionStale) return { c: '' };
    const form = s.form;
    if (!reelsSupported(form) || form.fields.some(field => field.key === 'text')) return { c: 'off', t: uiText(OFF) };
    const mode = h.mode();
    if (mode === 'one' && !h.pre().id) return { c: 'off', t: uiText(OFF) };
    if (f.policy) {
      const templates = h.needTs().flatMap(p => f.policy!.templates.filter(t => t.id === p.id));
      const { orphan } = screenRoles(form, f.policy, mode, mode === 'pack' || mode === 'app' ? [] : templates);
      if (orphan) return { c: 'off', t: uiFormat('Роль «{0}» пока недоступна', orphan) };
    }
    if (!form.available) return { c: 'unavailable', t: uiText('Создание сейчас недоступно') };
    if (mode === 'multi' && h.selPs().length < Math.max(2, f.policy?.multi?.min ?? 2)) return { c: 'sel' };
    if (h.pplTs().length) {
      if (h.P.missing()) return { c: 'cast' };
      for (const c of h.castFlat()) {
        if (c.type !== 'person') continue;
        const ref = personRef(c.id);
        if (!ref.ok) return { c: 'people', t: ref.text };
      }
      if (!keyInfo(form, JSON_CAST) && !form.fields.some(field => /^cast_/.test(field.key))) return { c: 'off', t: uiText(OFF) };
    }
    /* неподтверждённая загрузка: «Продолжить загрузку» (recovery) — кнопка активна; иначе — загрузка идёт */
    if (f.bridge.pending()) return f.bridge.stalled() && s.error === TRANSPORT && !s.busy ? { c: '' } : { c: 'upload', t: uiText('Загружаем фото…') };
    for (const x of h.objNeeds()) if ((S.obj[x.k]?.length ?? 0) < x.min) return { c: 'ph', x };
    if (form.chargeable !== false && displayPrice(f) !== d.price) return { c: 'price', t: uiText('Цена этого выбора пока не подтверждена') };
    return { c: '' };
  }
  /** Что починить вместо создания: форма не загрузилась или устарела — перечитать; загрузка не подтверждена — продолжить тем же id */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    return null;
  }
  /** Цена на кнопке: расчёт сервера под выбор (quote) — он и уходит в заказ; до него — цена каталога: шаблон (своя, иначе его типа),
      набор (сумма, от MULTI_FROM — скидка), пакет; сцена и работа без списания — цена сервиса. Неизвестна — цена сервиса.
      Цены каталога в документе экрана могут быть демо (на проде 190/150/90 при прайсе 285): без расчёта кнопка честно не подтверждена. */
  function displayPrice(f: Frame): number {
    const { s, d } = live(f);
    if (!s.form || s.form.chargeable === false || d.quoted) return d.price;
    const { TYPES } = h.tables;
    const priceOf = (p: TplPreset) => p.price ?? TYPES[p.t]?.price ?? null;
    const mode = h.mode();
    if (mode === 'multi') {
      const list = h.selPs().map(priceOf);
      if (!list.length || list.some(x => x === null)) return d.price;
      const o = h.offer();
      const sum = (list as number[]).reduce((a, b) => a + b, 0);
      return Math.round(sum * (list.length >= o.multiFrom ? 1 - o.multiOff : 1));
    }
    if (mode === 'pack') return h.tables.PRESETS.pack?.price ?? d.price;
    if (mode === 'one') return h.pre().id ? (priceOf(h.pre()) ?? d.price) : d.price;
    return d.price;
  }

  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  function formSync(f: Frame) {
    const { s, d } = live(f);
    const form = s.form;
    if (form && !f.form && f.formAt !== form) {
      /* первая форма: сервис ставит форму, затем её входы (подстановка входа и повтора) отдельными шагами одного прохода — сверка после всех */
      if (!f.formWait) {
        f.formWait = true;
        queueMicrotask(() => {
          f.formWait = false;
          f.formAt = f.svc.get().form;
          changed(f);
        });
      }
      return;
    }
    if (form && f.form !== form) {
      const first = !f.form;
      f.form = form;
      const fresh = load(f, form);
      if (first) {
        enter(f);
        adopt(f, s.inputs);
        f.prefill = f.known;
        if (onForm() && !swapping()) h.render();
        focusRole(f);
      } else if (fresh && onForm() && !swapping()) h.render();
      /* итог, нарисованный до формы: плитки «Ещё 3 варианта» и подписи — по политике формы */
      if (h.tail.run?.st === 'done') h.tail.refresh();
      f.readyDone();
    } else if (form && f.tables !== loaded) {
      load(f, form);
      if (onForm() && !swapping()) h.render();
    }
    if (!form) {
      if (s.error || s.integrityError) f.readyDone();
      return;
    }
    /* заявка, входы которой S не знает (перезагрузка, заявка родной формы): её вид — для формы и снимка хвоста */
    const it = intentOf(f);
    if (it && f.intentSeen !== it.key) {
      f.intentSeen = it.key;
      const inputs = encodeBack(form, (it.body.inputs ?? {}) as Record<string, unknown>);
      if (!f.known || !sameInputs(strip(f, inputs), f.known)) {
        adopt(f, inputs);
        /* страница заказа встала до формы (перезагрузка, заявка родной формы): снимок заказа и этапы — по входам заявки */
        resnap();
      }
    }
    /* входы сменились не зеркалом (повтор, «Изменить» после перезагрузки): вид в S и форма заново */
    if (!d.locked && f.known && !sameInputs(strip(f, s.inputs), f.known)) {
      if (f.prefill && sameInputs(strip(f, s.inputs), f.prefill)) {
        f.known = strip(f, s.inputs);
        f.mirror.seen(s.inputs);
      } else {
        adopt(f, s.inputs);
        if (onForm() && !swapping()) h.render();
      }
    }
    if (!onForm()) return;
    /* ошибка формы (не загрузилась, файлы повтора недоступны) — один раз подсказкой у кнопки */
    const error = s.integrityError ? '' : s.error;
    if (error !== f.said) {
      f.said = error;
      if (error && error !== TRANSPORT) tip(null, error);
    }
    commit(f);
    h.foot.sync();
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    formSync(f);
    f.bridge.sync();
    /* заявка без номера заказа (исход POST неизвестен): страница «Заказ не подтверждён» — когда форма пришла или не пришла */
    const s = f.svc.get();
    if (s.form || s.error || !s.intent || f.svc.derive(s).orderId) f.driver.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту) */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.foot.sync();
      });
  }

  /* ---- привязка кадра ---- */
  /** «Вы» и близкие сменились (server-state, листы людей): ряд лиц на месте, кнопка и входы — заново */
  let watching = false;
  function watch() {
    if (watching) return;
    watching = true;
    watchPeople({
      paint: () => {
        if (S.view !== 'cat' && h.pplTs().length) h.P.paintForm();
      },
      mounted: () => !!cur() && onForm(),
      changed: () => {
        commit();
        h.foot.sync();
      },
      m: peopleModule,
    });
  }
  /** «Назад» внутри экрана: пример на весь экран → лист людей → шаблон или набор, открытый из каталога, → каталог */
  function inner(): boolean {
    if (!h.mounted()) return false;
    if (h.PRV.el) {
      h.prvClose();
      return true;
    }
    if (h.P.isOpen) {
      h.P.close();
      return true;
    }
    if (h.tail.run || S.view === 'cat' || S.app || !S.fromCat) return false;
    h.backToCat();
    return true;
  }
  function bind(b: EngineBinding): EngineBound {
    watch();
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) dropFrame(frame);
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      back: () => (cur() === mine ? inner() : false),
      ready: () => mine.ready,
    };
  }
  function dropFrame(f: Frame) {
    f.bridge.cancel();
    f.result.release();
    f.polls.forEach(t => window.clearTimeout(t));
    f.polls.clear();
    f.consent?.close();
    f.consent = null;
    f.readyDone();
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    let resolve: () => void = () => {};
    const ready = new Promise<void>(r => {
      resolve = r;
    });
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      mirror: createInputsMirror(b.svc),
      form: null,
      policy: null,
      tables: '',
      known: null,
      prefill: null,
      intentSeen: '',
      formWait: false,
      formAt: null,
      me: meState(),
      openRole: null,
      castSig: '',
      skins: new Map(),
      trays: new Map(),
      restoring: new Set(),
      said: '',
      consent: null,
      peek: null,
      polls: new Set(),
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      ready,
      readyDone: () => resolve(),
    });
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      policy: key => objectPolicy(f, key),
      write: (key, value) => writeObjects(f, key, value),
      say: guard((text: string, key: string) => {
        const x = needOf(kindOfKey(key));
        if (x && onForm()) h.note(x, text);
        else tip(null, text);
      }),
      put: guard((item: MediaItem, tray: string) => putTile(f, item, tray)),
      drop: guard((item: MediaItem | null, key: string) => {
        dropTile(f, item, key);
        if (onForm()) h.foot.sync();
      }),
      update: guard(() => {
        if (onForm()) h.foot.sync();
      }),
    });
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => frame === f && bound === f && h.mounted(),
      localSteps,
      paintForm: () => h.render(),
      tip: (el, value) => tip(el, value),
      stepAlias: STEP_ALIAS,
    });
    f.result = createResultMedia({ svc: b.svc, ic: name => h.ic(name) });
    return f;
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр (painted); ход ведёт водитель сервиса кадра ---- */
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;
  /** Когда BotRun начал заказ («Создать», «Попробовать ещё раз»): его страница встаёт сменой SteosUI.swap через 170 мс и рисует RUN;
      закрыть итог (drop) или уйти к форме (exit) раньше — значит обнулить RUN под ждущей сменой (TypeError в таймере шаблона) */
  let startedAt = 0;
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });
  const frameOf = () => cur() ?? frame;
  /** Этапы шаблона (steps движка: люди или фото, сцены, монтаж, сборка) */
  function localSteps(): string[][] {
    const fn = tailOpts?.opts.steps;
    return typeof fn === 'function' ? (fn as () => string[][])() : [];
  }
  /** Ключи этапов сервера (cast · scenes · edit · pack, MessagingOrderSteps) → этапы шаблона (cast | file · shoot · edit · build) */
  const STEP_ALIAS: Record<string, string> = { scenes: 'shoot', pack: 'build' };
  Object.defineProperty(STEP_ALIAS, 'cast', { enumerable: true, get: () => localSteps()[0]?.[0] ?? 'cast' });
  /** Снимок заказа заново (вход заявки перенесён в S): заголовок, карточка, мозаика и этапы страницы */
  function resnap() {
    const r = h.tail.run;
    const snap = tailOpts?.opts.snap;
    if (!r || typeof snap !== 'function') return;
    r.snap = (snap as () => BotRunRun['snap'])();
    const steps = localSteps();
    if (steps.length === r.steps.length) r.steps = steps;
    if (h.mounted()) h.tail.refresh();
  }
  function failTextOf(r: BotRunRun): string | null {
    const s = frameOf()?.svc.get();
    const order = s?.order ?? null;
    const list = tailSteps(order);
    return failText(r, {
      order,
      error: s ? s.integrityError || s.error : '',
      texts: { modH: 'Фото не прошли проверку', techH: 'Не получилось собрать рилс', notMade: 'рилс не создан' },
      step: list.length ? list[tailCurrent(order, list)] : undefined,
    });
  }
  /** Заказ для чека: списано — основное и дозаказы вместе (charges[]: main и topup), иначе как у заказа */
  function charged(order: SpeechOrder | null): SpeechOrder | null {
    const list = rows(record(order).charges);
    const sum = list.reduce((a, c) => a + (Number.isFinite(Number(c.amount)) && Number(c.amount) > 0 ? Number(c.amount) : 0), 0);
    if (!order || !(sum > 0)) return order;
    return { ...order, charge: { ...record(record(order).charge), debited: sum } } as SpeechOrder;
  }
  const receiptOf = (r: BotRunRun) => receipt(r, charged(frameOf()?.svc.get().order ?? null), { cheap: false, where: '' });
  /** У формы есть каталог шаблонов (инструмент, не сцена): «Другой шаблон» ведёт в него */
  const hasCatalog = () => !S.app && h.tables.PL.some(p => p.id !== 'pack');
  /** Подпись под «… готовы»: у набора — сколько вариантов у каждого; «Ещё варианты…» — только если ряд их предлагает (родной хвост) */
  function doneText(r: BotRunRun): string {
    const multi = r.snap.mode === 'multi';
    const more = r.snap.mode !== 'pack' && server.groups(r).some(g => server.groupMore(r, g.gi) !== null);
    /* один ролик (решение 29.10.2026) — «прислал его», а не «их» */
    const one = !multi && server.groups(r).reduce((n, g) => n + g.items.length, 0) === 1;
    return [
      multi ? (h.offer().vars === 1 ? uiText('У каждого по одному ролику.') : uiFormat('У каждого по {0} варианта.', h.offer().vars)) : '',
      uiText(one ? 'Бот уже прислал его в чат, в отдельную тему.' : 'Бот уже прислал их в чат, в отдельную тему.'),
      more ? uiText('Ещё варианты можно добавить в конце ряда.') : '',
    ]
      .filter(Boolean)
      .join(' ');
  }
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    const next = typeof opts.next === 'function' ? (opts.next as (r: BotRunRun) => unknown[][]) : () => [];
    /* подписи хвоста шаблона (строки опций BotRun) — словарём профиля в момент показа: хвост живёт дольше языка профиля */
    const said = (value: unknown) => (typeof value === 'string' ? () => uiText(value) : value);
    const T = Object.fromEntries(Object.entries((opts.T ?? {}) as Record<string, unknown>).map(([key, value]) => [key, said(value)]));
    const foot = Object.fromEntries(
      Object.entries((opts.foot ?? {}) as Record<string, unknown>).map(([key, value]) => [key, Array.isArray(value) ? [value[0], value[1], said(value[2])] : value]),
    );
    inst = create({
      ...opts,
      drive: (r: BotRunRun) => {
        const f = cur();
        if (f) void f.driver.drive(r);
      },
      failText: (r: BotRunRun) => failTextOf(r),
      receipt: (r: BotRunRun) => receiptOf(r),
      foot: chatFoot(serverFoot(foot, 'rl'), 'rl'),
      /* «Другой шаблон» — только у формы с каталогом (родная форма: onOtherTemplate при каталоге) */
      next: (r: BotRunRun) =>
        next(r)
          .filter(item => item[0] !== 'data-rl-other' || hasCatalog())
          .map(item => [item[0], item[1], uiText(item[2])]),
      /* «Скачать» и «Ещё вариант» итога (res шаблона) — словарём профиля */
      acts: () => {
        const res = (opts.res ?? {}) as Record<string, unknown>;
        return [
          [res.save, 'download', uiText('Скачать')],
          [res.more, 'refresh', uiText('Ещё вариант')],
        ].filter(([attr]) => !!attr);
      },
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
      T: {
        ...T,
        doneP: (r: BotRunRun) => doneP(frameOf()?.svc.get().order, esc(doneText(r)), 'rl'),
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) => {
        const at = inst as unknown as Record<string, (...a: unknown[]) => unknown> & BotRunApi;
        const call = () => at[key](...args);
        if (key === 'start' || key === 'retry') {
          const out = call();
          if (at.run) startedAt = Date.now();
          return out;
        }
        /* итог закрывают (уход с экрана) или к форме уходят, пока страница заказа ещё встаёт: после смены, тем же экземпляром */
        const wait = startedAt + SWAP_GUARD_MS - Date.now();
        if ((key === 'drop' || key === 'exit') && wait > 0) {
          const r = at.run;
          window.setTimeout(() => {
            if (at.run !== r || (key === 'drop' && h.mounted())) return;
            call();
          }, wait);
          return undefined;
        }
        return call();
      };
    }
  }

  /* ---- итог: ряды роликов, обложки, «Ещё 3 варианта», «Скачать» ---- */
  /** Ряды итога — родная раскладка (resultRows: ряды video_set по шаблонам, остальные ролики — одним рядом), ключ ряда тот же */
  const linesOf = (order: SpeechOrder | null | undefined) => (order ? resultRows({ order } as unknown as CircleFormState) : []);
  interface ItemMeta {
    cap: string;
    sub: string;
    poster: string | null;
    dur: number | null;
    /** Шаблон ролика (template_id элемента: у пакета — свой у каждого ролика ряда) */
    tpl: string;
  }
  /** Кадр из ролика (GET …/result/files/{i}/poster): n8n обложку не присылает, без неё плитка показывала фото исполнителей вместо ролика.
      Кадр грузится с авторизацией один раз на файл; пришёл — заменяет слайд-шоу фото в плитке на месте */
  const posters = new Map<string, string | null>();
  const posterKey = (order: SpeechOrder, index: number) => `${order.id}:${order.result_revision ?? 1}:${index}`;
  function posterOf(order: SpeechOrder | null, index: number): string | null {
    if (!order || order.status !== 'done') return null;
    const key = posterKey(order, index);
    if (posters.has(key)) return posters.get(key) ?? null;
    posters.set(key, null);
    const revision = order.result_revision ?? 1;
    void endpoints.history
      .download(order.id, `/app/orders/${order.id}/result/files/${index}/poster?revision=${revision}`)
      .then(blob => {
        if (!blob.type.startsWith('image/')) return;
        const url = URL.createObjectURL(blob);
        posters.set(key, url);
        document.querySelectorAll<HTMLElement>(`[data-rl-poster="${CSS.escape(key)}"]`).forEach(el => {
          el.removeAttribute('data-rl-poster');
          el.innerHTML = `<img src="${h.esc(url)}" alt="" style="--i:0;object-position:50% 50%">`;
        });
      })
      .catch(() => {
        /* кадра нет (старый ролик, сбой) — остаются фото заказа, как раньше */
      });
    return null;
  }
  /** Подписи роликов сервера по индексу файла: caption, sub, обложка (poster_url, только безопасный адрес), длительность */
  function metaOf(order: SpeechOrder | null | undefined): Map<number, ItemMeta> {
    const out = new Map<number, ItemMeta>();
    let next = 0;
    for (const group of rows(record(order?.result?.data).groups))
      for (const item of rows(group.items)) {
        const index = Number.isInteger(item.index) ? (item.index as number) : next;
        next = index + 1;
        const ms = Number(item.duration_ms);
        out.set(index, {
          cap: text(item.caption),
          sub: text(item.sub),
          poster: safeMediaUrl(item.poster_url),
          dur: Number.isFinite(ms) && ms > 0 ? ms / 1000 : null,
          tpl: text(item.template_id),
        });
      }
    return out;
  }
  const doneOrder = (f: Frame | null) => {
    const order = f?.svc.get().order;
    return order?.status === 'done' && order.result?.available ? order : null;
  };
  /** Цена «Ещё 3 варианта» ряда group — правило родной формы: своя у шаблона, его типа, иначе формы; дозаказ не публикуется — null */
  function morePriceOf(f: Frame, group: string): number | null {
    const p = f.policy;
    if (!p?.moreAvailable) return null;
    /* сервер сверяет дозаказ со своим прайсом: цены типов в документе экрана — демо (на проде с ними был бы 409 price_changed) */
    if (p.serverMorePrice !== null) return p.serverMorePrice;
    const chosen = h.needTs().map(t => t.id);
    const t = p.templates.find(x => x.id === group) ?? (chosen.length === 1 ? p.templates.find(x => x.id === chosen[0]) : undefined);
    const own = t ? (t.morePrice ?? p.types[t.type]?.morePrice ?? null) : null;
    return own ?? p.morePrice;
  }
  const pendOf = (r: BotRunRun) => ((r as unknown as { pend?: Record<number, boolean> }).pend ??= {});
  /** Заголовок итога по новому числу роликов */
  function heading(r: BotRunRun) {
    const el = h.root()?.querySelector('#rlTx h2');
    const value = h.doneH(r);
    if (el && el.textContent !== value) el.textContent = value;
  }
  /** Ролики дозаказа: опрос заказа (GET, backoff) до роста ряда group; дольше MORE_WAIT_MS — заготовки снимаются, ролики придут в чат */
  function awaitMore(f: Frame, r: BotRunRun, gi: number, group: string, before: number) {
    const started = Date.now();
    let n = 0;
    const same = () => frame === f && h.tail.run === r;
    const finish = (grown: boolean) => {
      pendOf(r)[gi] = false;
      if (!same() || cur() !== f || !h.mounted()) return;
      h.resPaint(gi, grown);
      heading(r);
      h.rcPaint();
    };
    const tick = async () => {
      if (!same()) return;
      await f.svc.actions.refreshStatus().catch(() => undefined);
      if (!same()) return;
      const line = linesOf(doneOrder(f)).find(x => x.id === group);
      const grown = !!line && line.items.length > before;
      if (grown || Date.now() - started > MORE_WAIT_MS) finish(grown);
      else schedule();
    };
    const schedule = () => {
      const t = window.setTimeout(() => {
        f.polls.delete(t);
        void tick();
      }, MORE_DELAYS[Math.min(n++, MORE_DELAYS.length - 1)]);
      f.polls.add(t);
    };
    schedule();
  }
  /** Нажатие на ролик итога: файл заказа (owner-protected, один раз на id:revision:index) в рамке варианта, пауза — тем же нажатием */
  function play(id: string, btn: HTMLElement) {
    const f = cur();
    const r = h.tail.run;
    if (!f || !r || r.st !== 'done') return;
    const m = /_(\d+)_(\d+)$/.exec(id);
    const v = m ? server.groups(r)[Number(m[1])]?.items[Number(m[2])] : undefined;
    if (!v || v.index === undefined) return;
    const poster = metaOf(f.svc.get().order).get(v.index)?.poster ?? null;
    void f.result.toggle(v.index, { el: btn, kind: 'frame', dur: Number(v.it.d) || null, poster }).then(ok => {
      if (!ok && btn.isConnected) tip(btn, f.svc.get().error || 'Видео ещё загружается');
    });
  }


  const server: ReelsServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          /* кнопка чинит («Обновить форму», «Продолжить загрузку»), а не создаёт: «Не хватает · Пополнить» её не заслоняет */
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    entry: o => (passing && o && typeof o === 'object' ? (o as Record<string, unknown>) : {}),
    open() {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — пустой каталог без действий: missInfo держит «Проверяем доступность» */
        unload();
        return false;
      }
      const fresh = painted !== f;
      if (fresh) {
        /* другой кадр или новый сервис под прежним ключом: S, люди, «Вы» экрана, фото и хвост прежнего сюда не переходят */
        h.camStop();
        for (const [key, value] of Object.entries(JSON.parse(S0) as Record<string, unknown>)) (S as unknown as Record<string, unknown>)[key] = value;
        rebuild();
        painted = f;
        f.form = null;
        f.known = null;
        f.intentSeen = '';
        f.formAt = null;
        unload();
      }
      f.formShown = false;
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return true;
      }
      if (!fresh && f.form) {
        /* то же открытие того же кадра: таблицы его формы, правки человека остаются */
        load(f, f.form);
        h.P.prune();
      }
      /* сохранённая заявка этого приложения: страница заказа сразу (только показ; правду решит сервис), иначе форма */
      f.peek = peekIntent(f.app.id);
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
      return true;
    },
    close() {
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
      f.result.stop();
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    catReady() {
      /* «Выбрать несколько» — только у формы с набором шаблонов (template_ids и режим multi) */
      const f = cur();
      if (!canMulti(f?.svc.get().form)) h.root()?.querySelector('[data-rl-selmode]')?.remove();
    },
    /* шапка «Вы в кино» — только у сцены (policy.scene: свои роли апки); апка без своих ролей приходит с ролью «Вы» в app_cast
       и scene: false — у шаблона это «Рилс с вами» (formTitle: SCENES только у апки с app.cast), как и в ReelsOrderForm */
    scene: () => cur()?.policy?.scene === true,
    pre: () => h.tables.PL.find(p => p.t !== 'x') ?? STUB,
    missInfo,
    price() {
      const f = cur();
      return f && f.svc.get().form ? displayPrice(f) : 0;
    },
    label() {
      const f = cur();
      if (!f) return null;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return uiText('Продолжить проверку');
      if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
      if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
      if (f.bridge.pending()) return uiText('Загружаем фото…');
      return null;
    },
    commit: () => commit(),
    setItems,
    styles: () => [{ n: '', f: '' }],
    cover(p) {
      const list = p.frames?.length ? p.frames : [];
      if (!list.length) return [{ url: BLANK_FRAME, pos: 50, f: '' }];
      return [0, 1, 2].map(i => ({ url: list[i % list.length], pos: 50, f: '' }));
    },
    /** Веер пакета (и подложки набора): шаблоны эталона film · bw · vhs (bot-reels.js packHTML), если каталог их публикует;
        недостающие — первые шаблоны «с вами» по порядку каталога */
    fan(n = 3) {
      const ok = h.tables.PL.filter(p => p.nd === 'me' && p.t !== 'x');
      const pool = ok.length ? ok : h.tables.PL.filter(p => p.t !== 'x');
      const ids = pool.map(p => p.id);
      const list = FAN.filter(id => ids.includes(id));
      for (const id of ids) if (list.length < n && !list.includes(id)) list.push(id);
      return list.slice(0, n);
    },
    comparePrice() {
      const f = cur();
      const own = f?.policy?.pack?.comparePrice;
      if (typeof own === 'number') return own;
      const { TYPES } = h.tables;
      return (TYPES.n?.price ?? TYPES.x?.price ?? 0) * h.offer().packN;
    },
    tplPrice: p => {
      const form = cur()?.svc.get().form;
      return form?.chargeable ? form.expected_price : (p.price ?? h.tables.TYPES[p.t]?.price ?? 0);
    },
    appFrames() {
      const f = cur();
      const frames = f?.policy?.template?.frames ?? [];
      if (!frames.length) return [];
      if (frames.length === 1) return [42, 50, 58].map((x, i) => ({ k: `dapp0_${i}`, url: frames[0], pos: 20, x, f: '' }));
      return frames.map((url, i) => ({ k: `dapp0_${i}`, url, pos: 20, x: 50, f: '' }));
    },
    addFiles(k, list) {
      const f = cur();
      if (!f || h.tail.run) return;
      const x = needOf(k);
      const tray = trayOf(f, k);
      if (!x || !tray) return;
      const imgs = Array.from(list).filter(isImage);
      const free = Math.max(0, x.max - (S.obj[k]?.length ?? 0));
      const take = imgs.slice(0, free);
      if (take.length) {
        h.ui().buzz();
        void tray.pickSlots(take);
      }
      if (imgs.length > take.length)
        h.note(
          x,
          take.length
            ? uiFormat('Добавили {0} из {1}: шаблон берёт до {2} фото', take.length, imgs.length, x.max)
            : uiFormat('Шаблон берёт до {0} фото', x.max),
        );
    },
    removeFile(k, tile) {
      const f = cur();
      if (!f) return false;
      const { s, d } = live(f);
      if (d.submissionBlocked || s.intent) {
        const x = needOf(k);
        if (x) h.note(x, uiText(s.intent ? 'Заказ уже отправлен: дождитесь проверки' : WAIT));
        return false;
      }
      const tray = trayOf(f, k);
      if (tray && tile.m) tray.remove(tile.m);
      return true;
    },
    camOff(k) {
      const x = needOf(k);
      if (x) h.note(x, uiText('Камера недоступна. Загрузите файл.'));
    },
    meState: () => (cur() ?? frame)?.me ?? loose,
    blank: () => SILHOUETTE,
    start(btn) {
      const f = cur();
      if (!f) return;
      commit(f);
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      /* фото есть, а права на них не подтверждены (повтор): лист согласия, затем создание */
      const files = uploadKeys(s.form).some(key => !!s.inputs[key]);
      if (!s.rights && files) {
        f.consent = openConsentSheet({
          rights: () => f.svc.get().rights,
          locked: () => f.svc.derive().locked,
          setRights: on => f.svc.setters.rights(on),
          onContinue: () => {
            f.consent = null;
            if (cur() === f && onForm()) server.start(btn);
          },
          onClose: () => {
            f.consent = null;
          },
        });
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    /** Ряды итога из заказа (video_set): подписи и длительность сервера; шаблон снимка — для накладки и длительности */
    groups(r) {
      const tplItem = (id: string): Record<string, unknown> | null => {
        const p = h.tables.PRESETS[id];
        return p ? { id: p.id, n: p.n, nd: p.nd, t: p.t, f: p.f, ov: p.ov, tx: p.tx, d: p.d, slots: null } : null;
      };
      const order = doneOrder(frameOf());
      if (!order) return [];
      const sn = r.snap;
      const items = Array.isArray(sn.items) ? (sn.items as Array<Record<string, unknown>>) : [];
      const meta = metaOf(order);
      const lines = linesOf(order);
      return lines.map((line, gi) => {
        const base = items.find(x => x.id === line.id) ?? (lines.length === 1 ? items[0] : undefined) ?? { d: Number(sn.dur) || 0 };
        return {
          gi,
          id: line.id,
          h: lines.length > 1 ? line.title || text(base.n) : '',
          items: line.items.map((x, i) => {
            const m = meta.get(x.index);
            /* ролик своего шаблона (пакет): его запись заказа, иначе шаблон каталога; без template_id — шаблон ряда */
            const own = m?.tpl && m.tpl !== text(base.id) ? (items.find(y => y.id === m.tpl) ?? tplItem(m.tpl)) : null;
            const it = own ?? base;
            return {
              i,
              index: x.index,
              it: { ...it, d: m?.dur ?? (Number(it.d) || Number(sn.dur) || 0) },
              cap: m?.cap || uiFormat('Вариант {0}', i + 1),
              sub: m?.sub ?? '',
            };
          }),
        };
      });
    },
    /** Обложка ролика в разметке varPic шаблона (span.rl-ss.rl-rs + span.rl-dec): poster_url сервера — один кадр без цветовых
        вариантов демо; без обложки — фото заказа, из которых собран ролик (picsOf), со сдвигом по номеру варианта; демо-лица нет (D12) */
    pic(sn, v) {
      const order = frameOf()?.svc.get().order ?? null;
      const poster = v.index === undefined ? null : (metaOf(order).get(v.index)?.poster ?? posterOf(order, v.index));
      let list: Array<{ url: string; op: string; f: string }>;
      if (poster) list = [{ url: poster, op: '50% 50%', f: '' }];
      else {
        const L = h.picsOf(sn, v.it).filter(x => !!x.url);
        const k = L.length ? v.i % L.length : 0;
        list = L.slice(k).concat(L.slice(0, k));
      }
      if (!list.length) return '';
      const sl = ((Number(v.it.d) || 0) / Math.max(1, list.length)).toFixed(2);
      const imgs = list
        .map((x, i) => `<img src="${h.esc(x.url)}" alt="" style="--i:${i};object-position:${h.esc(x.op)}${x.f ? ';filter:' + h.esc(x.f) : ''}">`)
        .join('');
      const p = h.tables.PRESETS[text(v.it.id)];
      const wait = !poster && order && v.index !== undefined ? ` data-rl-poster="${h.esc(posterKey(order, v.index))}"` : '';
      return `<span class="rl-ss rl-rs"${wait} style="--sl:${sl}s">${imgs}</span><span class="rl-dec">${h.decHTML(p ?? { ov: '' })}</span>`;
    },
    groupMore(r, gi) {
      const f = frameOf();
      const order = doneOrder(f);
      if (!f || !order || r.snap.mode === 'pack') return null;
      const line = linesOf(order)[gi];
      return line ? morePriceOf(f, line.id) : null;
    },
    /** «Ещё 3 варианта» ряда gi: остаток известен и меньше — дрожь и подсказка; иначе заготовки ряда, дозаказ (reels-more: ключ до
        запроса, владелец заново, тот же ключ при повторе), затем опрос заказа до новых роликов. Отказ — заготовки уходят, подсказка */
    more(gi, btn) {
      const f = cur();
      const r = h.tail.run;
      const order = doneOrder(f);
      if (!f || !r || !order || r.st !== 'done' || r.snap.mode === 'pack' || pendOf(r)[gi]) return;
      const line = linesOf(order)[gi];
      const price = line ? morePriceOf(f, line.id) : null;
      if (!line || price === null) return;
      const left = circleBalance();
      if (left !== null && left < price) {
        (window.BotCore as unknown as { shake?(el: Element, buzz?: unknown): void } | undefined)?.shake?.(btn, h.ui().buzz);
        tip(btn, uiFormat('Не хватает {0} C. Пополните баланс', price - left));
        return;
      }
      const s = f.svc.get();
      pendOf(r)[gi] = true;
      h.ui().buzz();
      h.resPaint(gi, false);
      const before = line.items.length;
      void postMore({ orderId: order.id, group: line.id, price, owner: s.intent?.owner ?? s.identity?.owner }).then(outcome => {
        if (frame !== f || h.tail.run !== r) return;
        if (outcome === 'sent') {
          forgetCircleBalance();
          awaitMore(f, r, gi, line.id, before);
          return;
        }
        pendOf(r)[gi] = false;
        if (cur() !== f || !h.mounted()) return;
        h.resPaint(gi, false);
        tip(h.root()?.querySelector(`[data-rl-more="${gi}"]`) ?? null, MORE_NOTES[outcome]);
      });
    },
    /** «Сделать кружок»: как родной хвост — «Говорящий кружок» с роликом (без файла: маршрута сервера у рилса нет, Q5) */
    toCircle() {
      const r = h.tail.run;
      if (!r || r.st !== 'done') return;
      openStudioTarget({ screen: 'circle', who: 'video' });
    },
    /** «Скачать»: архив итога (archive_url, owner-protected путь заказа), иначе ролик, который смотрят (или первый) */
    save(btn) {
      const f = cur();
      const r = h.tail.run;
      if (!f || !r || r.st !== 'done') return;
      const ui = h.ui();
      const core = window.BotCore as unknown as { saveBtn(btn: HTMLElement, make: () => Promise<{ data: Blob; name: string }>, o: object): unknown };
      void core.saveBtn(
        btn,
        async () => {
          const order = doneOrder(f);
          if (!order) throw new Error('result_unavailable');
          const url = archiveUrl(order);
          if (url) return { data: await archive(order, url), name: 'steos-reels.zip' };
          const index = f.result.playing() ?? linesOf(order)[0]?.items[0]?.index;
          if (index === undefined) throw new Error('result_unavailable');
          return f.result.download(index);
        },
        { buzz: ui.buzz, tip: ui.tip },
      );
    },
    /** «Ещё вариант», «Заменить фото», «Другой шаблон»: заказ просмотрен, к форме (или каталогу) с теми же людьми и фото, без запроса (D10) */
    leave(go) {
      const f = cur();
      if (!f) return;
      f.result.stop();
      void f.driver.leave(go, false, '');
    },
    sortBtn() {
      const on = flt.sort !== 'pop' || flt.cast.length > 0;
      return `<button class="iconb rl-selb rl-sortb${on ? ' on' : ''}" type="button" data-rl-sort aria-haspopup="dialog" aria-label="${h.esc(uiText('Сортировка и фильтры'))}">${h.ic('sort')}</button>`;
    },
    catView,
    click(d, el, _r, e) {
      if (d.rlSort !== undefined) {
        openSort();
        return true;
      }
      const f = cur();
      if (d.rlChat !== undefined) {
        chatClick({ order: f?.svc.get().order, el, text: 'Тут откроется тема чата, куда бот прислал рилсы', tip: (at, value) => h.ui().tip(at, value) });
        return true;
      }
      if (d.rlSettings !== undefined) {
        openSettings();
        return true;
      }
      if (d.rvPlay !== undefined) {
        if (!el.closest('#rlRes')) return false;
        /* ролик итога: настоящий файл, а не имитация просмотра BotCore.rv */
        e?.stopPropagation();
        e?.preventDefault();
        play(d.rvPlay, el);
        return true;
      }
      if (d.rlRetry === undefined && d.rlRchk === undefined && d.rlFix === undefined) return false;
      if (!f || f.driver.busy()) return true;
      if (d.rlRetry !== undefined) h.tail.retry();
      else if (d.rlRchk !== undefined) void f.driver.check(el);
      else void f.driver.leave(() => h.tail.exit(() => h.render()), false, '');
      return true;
    },
  };

  /* смена языка профиля (steos:locale): хвост — этапы и страница заново, форма — таблицы языка (подписи документа) и вид заново */
  document.addEventListener('steos:locale', () => {
    const f = cur();
    const root = h.root();
    if (!f || !root || !h.mounted()) return;
    const r = h.tail.run;
    if (r) {
      const fresh = localSteps();
      for (const step of r.steps) {
        const same = fresh.find(item => item[0] === step[0]);
        if (same) {
          step[2] = same[2];
          step[3] = same[3];
        }
      }
      h.tail.show(root);
    } else {
      if (f.form) load(f, f.form);
      h.render();
    }
    changed(f);
  });
  /* новый снимок Me уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.foot.sync();
  });
  instance = { bind };
  return server;
}

/** ENGINES.reels.bind: привязка кадра к серверу экрана, созданному движком (без движка — пустая привязка) */
export function bindReelsEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «reels» реестра движков студии: движок шаблона window.BotReels, атрибуты data-rl-*, знак заказа — рилс */
export const REELS_ENGINE: EngineScreen = { global: 'BotReels', prefix: 'rl', side: false, kind: 'reels', bind: bindReelsEngine };

export function registerReelsEngine() {
  ENGINES.reels = REELS_ENGINE;
}

